import { useNavigate } from "react-router-dom";
import {
  Shield,
  Building2,
  ShieldCheck,
  AlertTriangle,
  ArrowRight,
} from "lucide-react";
import { FAQCategoryCard } from "@/components/faq/FAQCategoryCard";
import { FAQAccordion } from "@/components/faq/FAQAccordion";
import { Button } from "@/components/ui/button";
import { faqCategories, trendingQuestions } from "@/data/nis2-faq-data";

const NIS2FAQ = () => {
  const navigate = useNavigate();

  const categoryIcons = {
    compliance: Shield,
    entities: Building2,
    security: ShieldCheck,
    reporting: AlertTriangle,
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="container mx-auto px-4 py-6">
          <div className="space-y-2">
            <h1 className="text-3xl md:text-4xl font-bold text-foreground">
              NIS2 România - Ghid de Conformitate
            </h1>
            <p className="text-muted-foreground text-lg">
              Informații actuale conform OUG 155/2024
            </p>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-12 space-y-16">
        {/* Category Cards */}
        <section className="space-y-8">
          <div className="text-center space-y-2">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground">
              Explorează pe categorii
            </h2>
            <p className="text-muted-foreground">
              Găsește răspunsuri la întrebările tale despre conformitatea NIS2
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {faqCategories.map((category) => {
              const Icon =
                categoryIcons[category.id as keyof typeof categoryIcons];
              return (
                <FAQCategoryCard
                  key={category.id}
                  title={category.title}
                  icon={Icon}
                  description={category.description}
                  onClick={() =>
                    navigate("/nis2-faq/browse", {
                      state: { category: category.id },
                    })
                  }
                />
              );
            })}
          </div>

          <div className="flex justify-center">
            <Button
              onClick={() => navigate("/nis2-faq/browse")}
              variant="link"
              className="gap-2 text-base"
            >
              Toate întrebările
              <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        </section>

        {/* Trending Questions */}
        <section className="space-y-8">
          <div className="text-center space-y-2">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground">
              Întrebări Trending
            </h2>
            <p className="text-muted-foreground">
              Cele mai căutate întrebări despre NIS2 și OUG 155/2024
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <FAQAccordion
              questions={trendingQuestions}
              onQuestionClick={(id) => navigate(`/nis2-faq/answer/${id}`)}
            />
          </div>

          <div className="flex justify-center">
            <Button onClick={() => navigate("/nis2-faq/browse")} size="lg">
              Vezi toate întrebările
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </section>

        {/* Quick Info Section */}
        <section className="bg-primary/5 rounded-lg p-8 space-y-6">
          <h2 className="text-2xl font-bold text-foreground text-center">
            Informații importante despre NIS2
          </h2>
          <div className="grid md:grid-cols-3 gap-6 text-center">
            <div className="space-y-2">
              <div className="text-3xl font-bold text-primary">17 oct 2024</div>
              <p className="text-sm text-muted-foreground">
                Intrare în vigoare OUG 155/2024
              </p>
            </div>
            <div className="space-y-2">
              <div className="text-3xl font-bold text-primary">17 apr 2025</div>
              <p className="text-sm text-muted-foreground">
                Termen implementare măsuri minime
              </p>
            </div>
            <div className="space-y-2">
              <div className="text-3xl font-bold text-primary">10M EUR</div>
              <p className="text-sm text-muted-foreground">
                Amenzi până la sau 2% din CA anuală
              </p>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-border mt-16">
        <div className="container mx-auto px-4 py-8">
          <div className="text-center text-sm text-muted-foreground">
            <p>
              © 2024 NIS2 România. Toate informațiile sunt bazate pe OUG
              155/2024.
            </p>
            <p className="mt-2">
              Pentru consultanță oficială, contactați DNSC:{" "}
              <a
                href="https://www.dnsc.ro"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                www.dnsc.ro
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default NIS2FAQ;
